import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { getActiveDineOutletId } from 'auth/dineOutlet';
import type { DineCourse, DineMenuGroup, DineOutletItem, DineOutletStation, AddDineOutletItemsResult } from 'api/types';

/**
 * Add dishes from the menu master to the header outlet (DO-312). Pick dishes,
 * or take a whole group or course. A dish that would go to a department this
 * outlet does not use is refused with the reason; choosing a department here
 * sends every added dish there instead (the juice counter makes Masala Chaas at
 * the Juice Bar although the dish says Kitchen).
 */
interface Props {
  isOpen: boolean;
  outletName: string;
  courses: DineCourse[];
  groups: DineMenuGroup[];
  onClose: () => void;
  onAdded: () => void;
}

type Mode = 'dishes' | 'group' | 'course';

const DineOpsOutletItemAddModal: React.FC<Props> = ({ isOpen, outletName, courses, groups, onClose, onAdded }) => {
  const [mode, setMode] = useState<Mode>('dishes');
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<DineOutletItem[]>([]);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [groupId, setGroupId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [stationId, setStationId] = useState('');
  const [stations, setStations] = useState<DineOutletStation[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<AddDineOutletItemsResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setMode('dishes'); setSearch(''); setTicked(new Set()); setGroupId(''); setCourseId(''); setStationId('');
    setResult(null); setServerError(null);
    const outletId = getActiveDineOutletId();
    if (outletId) {
      dineOpsService.getOutletStations(outletId)
        .then((list) => setStations(list.filter((s) => s.isAllowed)))
        .catch(() => setStations([]));
    }
  }, [isOpen]);

  // Dishes this outlet does not sell yet, searched on the server.
  useEffect(() => {
    if (!isOpen || mode !== 'dishes') return;
    const t = setTimeout(() => {
      setLoading(true);
      dineOpsService.getOutletItems({ show: 'unlisted', searchTerm: search.trim() || undefined, page: 1, pageSize: 50 })
        .then((res) => setCandidates(res.data))
        .catch((err: any) => setServerError(err?.response?.data?.message ?? 'The menu could not be loaded.'))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [isOpen, mode, search]);

  const toggle = (id: string) => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const ready = (mode === 'dishes' && ticked.size > 0) || (mode === 'group' && !!groupId) || (mode === 'course' && !!courseId);
  const what = mode === 'dishes'
    ? `${ticked.size} dish${ticked.size === 1 ? '' : 'es'}`
    : mode === 'group'
      ? `every dish in ${groups.find((g) => String(g.id) === groupId)?.groupName ?? 'the group'}`
      : `every dish in ${courses.find((c) => String(c.id) === courseId)?.courseName ?? 'the category'}`;

  const stationOptions = useMemo(() => stations.map((s) => ({ value: s.stationId, label: s.stationName })), [stations]);

  const add = useCallback(async () => {
    setConfirmOpen(false);
    setSaving(true);
    setServerError(null);
    try {
      const r = await dineOpsService.addOutletItems({
        itemIds: mode === 'dishes' ? [...ticked] : undefined,
        groupId: mode === 'group' ? groupId : undefined,
        courseId: mode === 'course' ? courseId : undefined,
        stationId: stationId || undefined,
      });
      setSaving(false);
      onAdded();
      if (r.refused.length === 0) {
        notificationService.toast.success(`${r.added} dish${r.added === 1 ? '' : 'es'} added to ${outletName}.`);
        onClose();
      } else {
        setResult(r); // keep the modal open so the refusals can be read
        setTicked(new Set());
      }
    } catch (err: any) {
      setSaving(false);
      setServerError(err?.response?.data?.message ?? 'The dishes could not be added.');
    }
  }, [mode, ticked, groupId, courseId, stationId, outletName, onAdded, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto', maxWidth: 720 }}>
        {saving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Adding...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Add dishes to {outletName}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {serverError && <div className="form-error-message" style={{ marginBottom: 12 }}>{serverError}</div>}
          {result && (
            <div data-add-result style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 6 }}>
              <div><strong>{result.added}</strong> added{result.alreadyListed ? `, ${result.alreadyListed} already sold here` : ''}.
                {' '}<strong>{result.refused.length}</strong> not added:</div>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13 }}>
                {result.refused.map((r) => <li key={r.itemId}><strong>{r.itemName}</strong> — {r.reason}</li>)}
              </ul>
            </div>
          )}

          <div className="form-group" style={{ display: 'flex', gap: 16 }} role="radiogroup" aria-label="What to add">
            {([['dishes', 'Pick dishes'], ['group', 'A whole group'], ['course', 'A whole category']] as [Mode, string][]).map(([m, label]) => (
              <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" name="add-mode" checked={mode === m} onChange={() => setMode(m)} /> {label}
              </label>
            ))}
          </div>

          {mode === 'dishes' && (
            <>
              <div className="form-group" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input className="form-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search dish, code, group or category"
                  aria-label="Search the menu" style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{ticked.size} ticked</span>
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                {loading && <div style={{ padding: 16, color: 'var(--text-muted)' }}>Loading...</div>}
                {!loading && candidates.length === 0 && (
                  <div style={{ padding: 16, color: 'var(--text-muted)' }}>{search ? 'No dish matches.' : `Every dish on the menu is already sold in ${outletName}.`}</div>
                )}
                {!loading && candidates.map((c) => (
                  <label key={c.itemId} data-candidate-row style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={ticked.has(c.itemId)} onChange={() => toggle(c.itemId)} aria-label={`Add ${c.itemName}`} />
                    <span style={{ flex: 1 }}>
                      <strong>{c.itemName}</strong>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {c.courseName} · {c.groupName}</span>
                    </span>
                    <span style={{ fontSize: 12, color: c.stationAllowed || c.itemType === 'OTHER' ? 'var(--text-secondary)' : 'var(--color-danger, #c0392b)' }}
                      title={c.stationAllowed ? undefined : 'This outlet does not use this kitchen station; choose a kitchen station here below'}>
                      {c.effectiveStationName ?? '—'} · {c.isOpenPrice ? 'Open' : Number(c.basePrice).toFixed(2)}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}

          {mode === 'group' && (
            <div className="form-group">
              <SyutSelect label="Menu group" options={groups.map((g) => ({ value: String(g.id), label: `${g.courseName} · ${g.groupName}` }))}
                value={groupId} onChange={(v) => setGroupId((v as string) || '')} placeholder="Choose a group" size="sm" menuPortalTarget={document.body} />
            </div>
          )}
          {mode === 'course' && (
            <div className="form-group">
              <SyutSelect label="Category" options={courses.map((c) => ({ value: String(c.id), label: c.courseName }))}
                value={courseId} onChange={(v) => setCourseId((v as string) || '')} placeholder="Choose a category" size="sm" menuPortalTarget={document.body} />
            </div>
          )}

          <div className="form-group" style={{ marginTop: 12 }}>
            <SyutSelect label="Kitchen station here (optional)" options={stationOptions} value={stationId}
              onChange={(v) => setStationId((v as string) || '')} placeholder="As the dish says" size="sm" isClearable menuPortalTarget={document.body} />
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Leave empty to send each dish where the menu says. Only kitchen stations {outletName} uses are listed.
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>{result ? 'Close' : 'Cancel'}</button>
          <button className="btn btn-primary" onClick={() => setConfirmOpen(true)} disabled={!ready || saving}>Add</button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen} title="Add dishes"
        message={`Sell ${what} in ${outletName}${stationId ? `, made at ${stations.find((s) => s.stationId === stationId)?.stationName ?? 'the chosen kitchen station'}` : ''}?`}
        confirmLabel="Add" cancelLabel="Cancel" variant="default" onConfirm={() => void add()} onCancel={() => setConfirmOpen(false)} />
    </>
  );
};

export default DineOpsOutletItemAddModal;
